import React, { useEffect, useState, useMemo } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Container from 'react-bootstrap/Container';
import Card from 'react-bootstrap/Card';
import Table from 'react-bootstrap/Table';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Alert from 'react-bootstrap/Alert';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import api from '../utils/api';
import PaymentForm from '../components/PaymentForm';
import { getStatusConfig } from '../utils/orderStatusConstants';
import './OrderDetail.css';

const formatCurrency = (value) => {
  if (value == null) return '-';
  return `฿${parseFloat(value).toLocaleString()}`;
};

export default function OrderDetail() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [feedback, setFeedback] = useState({ type: '', message: '' });
  const [isCancelling, setIsCancelling] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  const paymentInfo = useMemo(() => {
    const method = order?.payment_method ? String(order.payment_method).toLowerCase() : '';
    if (!order) {
      return { type: 'cash', label: '-', note: '' };
    }
    if (method.includes('cod') || method.includes('ปลายทาง') || method.includes('delivery')) {
      return {
        type: 'cod',
        label: 'เก็บเงินปลายทาง',
        note: 'ชำระเมื่อรับสินค้าและกรุณาเตรียมยอดให้ครบถ้วน'
      };
    }
    if (method.includes('install')) {
      return { type: 'installment', label: 'ผ่อนชำระ', note: '' };
    }
    return { type: 'cash', label: 'ชำระเต็มจำนวน', note: '' };
  }, [order]);

  const isInstallmentOrder = useMemo(() => paymentInfo.type === 'installment', [paymentInfo]);

  const summaryColProps = useMemo(() => (isInstallmentOrder ? { md: 12, lg: 4 } : { md: 12, lg: 6 }), [isInstallmentOrder]);
  const shippingColProps = useMemo(() => (isInstallmentOrder ? { md: 12, lg: 4 } : { md: 12, lg: 6 }), [isInstallmentOrder]);
  const installmentColProps = useMemo(() => ({ md: 12, lg: 4 }), []);
  const detailColProps = useMemo(() => (isInstallmentOrder ? { lg: 6 } : { lg: 12 }), [isInstallmentOrder]);

  const shippingInfo = useMemo(() => {
    const shipping = order?.shipping_address || {};
    const name = [shipping.recipientName, shipping.recipientSurname].filter(Boolean).join(' ').trim();
    const phone = typeof shipping.phone === 'string' ? shipping.phone.trim() : '';
    const address = typeof shipping.address === 'string' ? shipping.address.trim() : '';
    const telLink = phone ? phone.replace(/[^0-9+]/g, '') : '';
    return {
      name: name || '-',
      phone: phone || '-',
      address: address || '-',
      telLink: telLink || ''
    };
  }, [order]);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const response = await api.get(`/api/orders/${orderId}`);
        setOrder(response.data);
      } catch (err) {
        console.error('Error fetching order detail:', err);
        const message = err.response?.data?.error || 'ไม่สามารถโหลดข้อมูลคำสั่งซื้อได้';
        setError(message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchOrder();
  }, [orderId]);

  const handleCancelOrder = async () => {
    if (!order || order.order_status !== 'pending') {
      return;
    }
    const confirmed = window.confirm('ยืนยันการยกเลิกคำสั่งซื้อนี้หรือไม่?');
    if (!confirmed) {
      return;
    }

    setIsCancelling(true);
    setFeedback({ type: '', message: '' });

    try {
      const response = await api.put(`/api/orders/cancel/${order.order_id}`);
      setOrder((prev) =>
        prev
          ? {
              ...prev,
              order_status: response.data?.status || 'cancelled_by_customer'
            }
          : prev
      );
      setFeedback({ type: 'success', message: response.data?.message || 'ยกเลิกคำสั่งซื้อสำเร็จ' });
    } catch (err) {
      console.error('Cancel order error:', err);
      setFeedback({
        type: 'danger',
        message: err.response?.data?.error || 'ไม่สามารถยกเลิกคำสั่งซื้อได้'
      });
    } finally {
      setIsCancelling(false);
    }
  };

  const installmentSummary = useMemo(() => {
    if (
      !order ||
      !isInstallmentOrder ||
      !Array.isArray(order.installments) ||
      order.installments.length === 0
    ) {
      return null;
    }

    const paidCount = order.installments.filter((item) => item.payment_status === 'paid').length;
    return {
      total: order.installments.length,
      paid: paidCount,
      remaining: order.installments.length - paidCount
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order]);

  // Check if there's a pending payment for a specific installment
  const hasPendingPayment = (installmentId) => {
    if (!order?.payments || !Array.isArray(order.payments)) return false;
    return order.payments.some(
      p => p.installment_id === installmentId && p.status === 'pending'
    );
  };

  // Check if there's a pending payment for full payment (non-installment)
  const hasPendingFullPayment = () => {
    if (!order?.payments || !Array.isArray(order.payments)) return false;
    return order.payments.some(
      p => !p.installment_id && p.status === 'pending'
    );
  };

  // Check if payment button should be shown
  const shouldShowPaymentButton = useMemo(() => {
    if (!order || order.order_status !== 'awaiting_payment') {
      return false;
    }

    // For COD orders, don't show payment button
    if (paymentInfo.type === 'cod') {
      return false;
    }

    // For cash orders: Show button only if no pending payment exists
    if (!isInstallmentOrder) {
      if (!order.payments || !Array.isArray(order.payments)) {
        return true; // No payments yet, show button
      }
      const hasPendingPayment = order.payments.some(
        p => !p.installment_id && p.status === 'pending'
      );
      return !hasPendingPayment; // Show button only if no pending payment exists
    }

    // For installment orders: Show button only if first installment hasn't been paid or has pending payment
    if (isInstallmentOrder) {
      const firstInstallment = order.installments?.find(
        inst => inst.installment_number === 1
      );
      
      if (!firstInstallment) {
        return false; // No first installment found
      }

      // Check if first installment is paid
      if (firstInstallment.payment_status === 'paid') {
        return false; // First installment already paid, don't show button
      }

      // Check if there's a pending payment for first installment
      const hasPendingFirstPayment = order.payments?.some(
        p => p.installment_id === firstInstallment.installment_id && p.status === 'pending'
      );

      if (hasPendingFirstPayment) {
        return false; // Already submitted payment for first installment
      }

      return true; // First installment not paid and no pending payment, show button
    }

    return false;
  }, [order, isInstallmentOrder, paymentInfo]);

  // Get payment amount and installment info for the modal
  const getPaymentInfo = useMemo(() => {
    if (!order) return { amount: null, installmentNumber: null, installmentId: null };

    if (!isInstallmentOrder) {
      // For cash orders, use total amount
      return {
        amount: order.total_amount,
        installmentNumber: null,
        installmentId: null
      };
    }

    // For installment orders, get first installment info
    const firstInstallment = order.installments?.find(
      inst => inst.installment_number === 1
    );

    if (!firstInstallment) {
      return { amount: null, installmentNumber: null, installmentId: null };
    }

    return {
      amount: firstInstallment.installment_amount,
      installmentNumber: 1,
      installmentId: firstInstallment.installment_id,
      totalInstallments: order.installment_periods
    };
  }, [order, isInstallmentOrder]);

  const handleOpenPaymentModal = () => {
    setShowPaymentModal(true);
  };

  const handleClosePaymentModal = () => {
    setShowPaymentModal(false);
  };

  const handlePaymentSuccess = () => {
    // Refresh order data after successful payment submission
    const fetchOrder = async () => {
      try {
        const response = await api.get(`/api/orders/${orderId}`);
        setOrder(response.data);
        setFeedback({ type: 'success', message: 'แจ้งชำระเงินสำเร็จ รอการอนุมัติจากแอดมิน' });
      } catch (err) {
        console.error('Error fetching order detail:', err);
      }
    };
    fetchOrder();
    setShowPaymentModal(false);
  };


  // ตรวจสอบว่าการชำระงวดแรกเสร็จแล้วหรือยัง (สำหรับ installment order)
  const isFirstInstallmentPaid = useMemo(() => {
    if (!isInstallmentOrder || !order?.installments) return false;
    const firstInstallment = order.installments.find(inst => inst.installment_number === 1);
    return firstInstallment?.payment_status === 'paid';
  }, [order, isInstallmentOrder]);

  // คำนวณสถานะที่ควรแสดง (สำหรับ installment order ที่งวดแรกชำระแล้ว ให้แสดง "กำลังจัดส่ง")
  const getDisplayStatus = () => {
    if (isInstallmentOrder && isFirstInstallmentPaid) {
      return 'waiting_for_delivery';
    }
    return order.order_status;
  };

  const getStatusBadge = (status) => {
    const config = getStatusConfig(status);
    const classes = ['order-status-badge'];
    if (status === 'pending') {
      classes.push('order-status-badge--pending');
    }
    if (status === 'awaiting_payment') {
      classes.push('order-status-badge--awaiting');
    }
    if (status === 'waiting_for_delivery') {
      classes.push('order-status-badge--shipping');
    }
    if (status === 'completed') {
      classes.push('order-status-badge--completed');
    }
    if (status === 'cancelled' || status === 'cancelled_by_customer') {
      classes.push('order-status-badge--cancelled');
    }
    return (
      <Badge
        bg={config.variant}
        className={`px-3 py-2 fs-6 fw-semibold rounded-pill ${classes.join(' ')}`}
      >
        {config.text}
      </Badge>
    );
  };

  if (isLoading) {
    return (
      <Container className="my-5">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="my-5">
        <Alert variant="danger">
          <h4 className="mb-3">มีข้อผิดพลาด</h4>
          <p>{error}</p>
          <div className="d-flex gap-2">
            <Button variant="primary" onClick={() => navigate(-1)}>
              ย้อนกลับ
            </Button>
            <Link to="/orders" className="btn btn-outline-secondary">
              ไปยังรายการคำสั่งซื้อ
            </Link>
          </div>
        </Alert>
      </Container>
    );
  }

  if (!order) {
    return null;
  }

  return (
    <div className="order-detail-page">
      <Container>
        <Card className="order-detail-card">
          <div className="order-detail-card__header">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
              <div>
                <h2 className="order-detail-card__title">รายละเอียดคำสั่งซื้อ #{order.order_id}</h2>
                <div className="order-detail-card__meta">
                  <span>{new Date(order.order_date).toLocaleDateString('th-TH')}</span>
                  {getStatusBadge(getDisplayStatus())}
                </div>
              </div>
              <div className="d-flex gap-2">
                <Link to="/orders" className="btn btn-outline-secondary">
                  <i className="bi bi-arrow-left me-2"></i>
                  กลับไปหน้าคำสั่งซื้อ
                </Link>
              </div>
            </div>
          </div>

          <div className="order-detail-card__body">
            {feedback.message && (
              <Alert variant={feedback.type} onClose={() => setFeedback({ type: '', message: '' })} dismissible>
                {feedback.message}
              </Alert>
            )}

            <Row className="g-4 mb-4">
              <Col {...summaryColProps}>
                <Card className="order-detail__summary-card h-100">
                  <Card.Body>
                    <h5 className="mb-3">สรุปคำสั่งซื้อ</h5>
                    {order.order_status === 'awaiting_payment' && !isInstallmentOrder && hasPendingFullPayment() && (
                      <div className="order-detail-payment-submitted-text mb-3">
                        ส่งสลิปชำระเงินแล้ว
                      </div>
                    )}
                    <dl className="row mb-0">
                      <dt className="col-sm-5">ยอดรวม</dt>
                      <dd className="col-sm-7">{formatCurrency(order.total_amount)}</dd>
                      <dt className="col-sm-5">วิธีชำระเงิน</dt>
                      <dd className="col-sm-7">{paymentInfo.label}</dd>
                      <dt className="col-sm-5">จำนวนงวด</dt>
                      <dd className="col-sm-7">
                        {isInstallmentOrder
                          ? `${order.installment_periods} งวด`
                          : paymentInfo.type === 'cod'
                          ? 'เก็บเงินเพียงครั้งเดียวตอนรับสินค้า'
                          : 'ชำระเต็มจำนวน'}
                      </dd>
                      <dt className="col-sm-5">ยอดผ่อนต่อเดือน</dt>
                      <dd className="col-sm-7">
                        {isInstallmentOrder ? formatCurrency(order.monthly_payment) : '-'}
                      </dd>
                      {paymentInfo.note && (
                        <>
                          <dt className="col-sm-5">หมายเหตุ</dt>
                          <dd className="col-sm-7">{paymentInfo.note}</dd>
                        </>
                      )}
                      <dt className="col-sm-5">สถานะล่าสุด</dt>
                      <dd className="col-sm-7">
                        {getStatusConfig(getDisplayStatus()).label}
                      </dd>
                    </dl>
                  </Card.Body>
                </Card>
              </Col>
              <Col {...shippingColProps}>
                <Card className="order-detail__summary-card h-100">
                  <Card.Body>
                    <h5 className="mb-3">ข้อมูลผู้รับและที่อยู่จัดส่ง</h5>
                    <dl className="row mb-0">
                      <dt className="col-sm-4">ชื่อผู้รับ</dt>
                      <dd className="col-sm-8">{shippingInfo.name}</dd>
                      <dt className="col-sm-4">เบอร์ติดต่อ</dt>
                      <dd className="col-sm-8">
                        {shippingInfo.phone !== '-' ? (
                          <a
                            href={shippingInfo.telLink ? `tel:${shippingInfo.telLink}` : undefined}
                            className="order-detail__contact-link"
                          >
                            {shippingInfo.phone}
                          </a>
                        ) : (
                          '-'
                        )}
                      </dd>
                      <dt className="col-sm-4">ที่อยู่จัดส่ง</dt>
                      <dd className="col-sm-8">
                        {shippingInfo.address !== '-' ? (
                          <span className="order-detail__shipping-address">{shippingInfo.address}</span>
                        ) : (
                          <span className="text-muted">ยังไม่ได้ระบุที่อยู่จัดส่ง</span>
                        )}
                      </dd>
                    </dl>
                  </Card.Body>
                </Card>
              </Col>
              {isInstallmentOrder && (
                <Col {...installmentColProps}>
                  <Card className="order-detail__summary-card h-100">
                    <Card.Body>
                      <h5 className="mb-3">ข้อมูลการผ่อนชำระ</h5>
                      {installmentSummary ? (
                        <dl className="row mb-0">
                          <dt className="col-sm-5">จำนวนงวดทั้งหมด</dt>
                          <dd className="col-sm-7">{installmentSummary.total} งวด</dd>
                          <dt className="col-sm-5">ชำระแล้ว</dt>
                          <dd className="col-sm-7">{installmentSummary.paid} งวด</dd>
                          <dt className="col-sm-5">คงเหลือ</dt>
                          <dd className="col-sm-7">{installmentSummary.remaining} งวด</dd>
                          <dt className="col-sm-5">ยอดรวมผ่อน</dt>
                          <dd className="col-sm-7">{formatCurrency(order.monthly_payment * installmentSummary.total)}</dd>
                        </dl>
                      ) : (
                        <p className="mb-0 text-muted">ไม่มีข้อมูลการผ่อนชำระ</p>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              )}
            </Row>

            <Card className="mb-4 order-detail__section-card">
              <Card.Body>
                <Row className="g-4">
                  <Col {...detailColProps}>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h5 className="mb-0">รายการสินค้า</h5>
                      <Badge bg="light" text="dark">
                        {order.details.length} รายการ
                      </Badge>
                    </div>
                    <Table responsive hover className="order-detail__products mb-0">
                      <thead>
                        <tr>
                          <th>สินค้า</th>
                          <th>ราคา</th>
                          <th>จำนวน</th>
                          <th className="text-end">รวม</th>
                        </tr>
                      </thead>
                      <tbody>
                        {order.details.map((item) => (
                          <tr key={item.order_detail_id}>
                            <td>
                              <div className="d-flex align-items-center gap-3">
                                <div className="order-detail__thumb">
                                  <img src={item.product_image} alt={item.product_name} />
                                </div>
                                <div>
                                  <strong>{item.product_name}</strong>
                                </div>
                              </div>
                            </td>
                            <td>{formatCurrency(item.price)}</td>
                            <td>{item.quantity}</td>
                            <td className="text-end">{formatCurrency(item.price * item.quantity)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Col>
                  {isInstallmentOrder && (
                    <Col lg={6}>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5 className="mb-0">ตารางการผ่อนชำระ</h5>
                        {installmentSummary && (
                          <Badge bg={installmentSummary.remaining === 0 ? 'success' : 'warning'}>
                            ชำระแล้ว {installmentSummary.paid}/{installmentSummary.total} งวด
                          </Badge>
                        )}
                      </div>
                      {order.installments?.length ? (
                        <Table responsive hover className="mb-0 order-detail__installments">
                          <thead>
                            <tr>
                              <th>งวดที่</th>
                              <th>ยอดชำระ</th>
                              <th>กำหนดชำระ</th>
                              <th>วันที่ชำระ</th>
                              <th>สถานะ</th>
                            </tr>
                          </thead>
                          <tbody>
                            {order.installments.map((item) => (
                              <tr key={item.installment_id}>
                                <td>{item.installment_number}</td>
                                <td>{formatCurrency(item.installment_amount)}</td>
                                <td>{new Date(item.payment_due_date).toLocaleDateString('th-TH')}</td>
                                <td>
                                  {item.payment_date
                                    ? new Date(item.payment_date).toLocaleDateString('th-TH')
                                    : '-'}
                                </td>
                                <td>
                                  <div>
                                    <Badge bg={item.payment_status === 'paid' ? 'success' : 'secondary'}>
                                      {item.payment_status === 'paid' ? 'ชำระแล้ว' : 'รอชำระ'}
                                    </Badge>
                                    {hasPendingPayment(item.installment_id) && (
                                      <Alert variant="info" className="mt-2 mb-0" style={{ fontSize: '0.85rem' }}>
                                        <i className="bi bi-info-circle me-1"></i>
                                        รอการอนุมัติจากแอดมิน
                                      </Alert>
                                    )}
                                    {(() => {
                                      const rejectedPayments = order.payments?.filter(
                                        p => p.installment_id === item.installment_id && p.status === 'rejected'
                                      ) || [];
                                      
                                      if (rejectedPayments.length > 0) {
                                        // แสดงเฉพาะ rejection reason ล่าสุด
                                        const latestRejection = rejectedPayments.sort((a, b) => 
                                          new Date(b.created_at) - new Date(a.created_at)
                                        )[0];
                                        
                                        return (
                                          <Alert variant="danger" className="mt-2 mb-0" style={{ 
                                            fontSize: '0.85rem',
                                            padding: '0.5rem 0.75rem',
                                            borderRadius: '0.375rem'
                                          }}>
                                            <div className="d-flex align-items-start">
                                              <i className="bi bi-exclamation-triangle-fill me-2 mt-1"></i>
                                              <div className="flex-grow-1">
                                                <strong className="d-block mb-1">การชำระเงินถูกปฏิเสธ</strong>
                                                <span>{latestRejection.rejection_reason || 'ไม่มีเหตุผล'}</span>
                                              </div>
                                            </div>
                                          </Alert>
                                        );
                                      }
                                      return null;
                                    })()}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      ) : (
                        <p className="mb-0 text-muted">ไม่มีข้อมูลงวดการผ่อนชำระ</p>
                      )}
                    </Col>
                  )}
                </Row>
              </Card.Body>
            </Card>
          </div>

          {order.order_status === 'pending' && (
            <div className="order-detail__footer">
              <Button
                variant="danger"
                size="lg"
                className="order-detail__cancel-btn"
                onClick={handleCancelOrder}
                disabled={isCancelling}
              >
                {isCancelling ? 'กำลังยกเลิก...' : 'ยกเลิกคำสั่งซื้อ'}
              </Button>
            </div>
          )}

          {/* Show rejected payment alerts for full payment */}
          {(() => {
            const rejectedPayments = (!isInstallmentOrder && order.payments?.filter(
              p => !p.installment_id && p.status === 'rejected'
            )) || [];
            
            if (rejectedPayments.length > 0) {
              // แสดงเฉพาะ rejection reason ล่าสุด
              const latestRejection = rejectedPayments.sort((a, b) => 
                new Date(b.created_at) - new Date(a.created_at)
              )[0];
              
              return (
                <Alert variant="danger" className="mt-3" style={{ 
                  padding: '0.75rem 1rem',
                  borderRadius: '0.5rem'
                }}>
                  <div className="d-flex align-items-start">
                    <i className="bi bi-exclamation-triangle-fill me-2 mt-1 fs-5"></i>
                    <div className="flex-grow-1">
                      <strong className="d-block mb-1 fs-6">การชำระเงินถูกปฏิเสธ</strong>
                      <span>{latestRejection.rejection_reason || 'ไม่มีเหตุผล'}</span>
                    </div>
                  </div>
                </Alert>
              );
            }
            return null;
          })()}

          {/* Payment button for awaiting_payment orders */}
          {shouldShowPaymentButton && (
            <div className="order-detail__footer">
              <Button
                variant="primary"
                size="lg"
                className="order-detail__pay-btn"
                onClick={handleOpenPaymentModal}
              >
                <i className="bi bi-qr-code me-2" />
                {isInstallmentOrder ? 'ชำระเงิน (งวดที่ 1)' : 'ชำระเงิน'}
              </Button>
            </div>
          )}

          {/* Show message if payment already submitted (pending) - แต่ไม่แสดงถ้าเป็น installment order ที่งวดแรกชำระแล้ว */}
          {order.order_status === 'awaiting_payment' && !shouldShowPaymentButton && !(isInstallmentOrder && isFirstInstallmentPaid) && (
            <div className="order-detail__footer">
              <Alert variant="info" className="mb-0">
                <i className="bi bi-info-circle me-2"></i>
                {isInstallmentOrder 
                  ? 'ส่งสลิปชำระเงินแล้ว รอการอนุมัติจากแอดมิน หรือไปชำระงวดถัดไปที่หน้าบัตร'
                  : 'ส่งสลิปชำระเงินแล้ว รอการอนุมัติจากแอดมิน'
                }
              </Alert>
            </div>
          )}

        </Card>
      </Container>

      <PaymentForm
        show={showPaymentModal}
        onHide={handleClosePaymentModal}
        orderId={order?.order_id}
        installmentId={getPaymentInfo.installmentId}
        installmentNumber={getPaymentInfo.installmentNumber}
        amount={getPaymentInfo.amount}
        totalInstallments={getPaymentInfo.totalInstallments}
        onSuccess={handlePaymentSuccess}
      />
    </div>
  );
}

